import {
  CategoricalDomain,
  CategoricalFilter,
  DataValue,
  Filter,
  FilterType,
  HierarchicalDataValue,
  HierarchicalFilter,
  HierarchicalLevelInfo,
  RangeDomain,
  RangeFilter,
  RelativeDateFilter,
} from '@tableau/api-internal-contract-js';
import {
  DataType,
  FiltersQuantitativeIncludedValues as IncludedValues,
  FilterType as PresLayerFilterType,
} from '../../gen-src/preslayer/Enums';
import {
  CategoricalDomainPresModel,
  FilterInfoPresModel,
  FilterValuePresModel,
  HierarchicalFilterValuePresModel,
  HierarchicalLevelInfoPresModel,
  QuantitativeDomainPresModel,
} from '../../gen-src/preslayer/PresModels';
import { PresLayerToApiEnumMappings as EnumMappings } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';
import { PresLayerToSharedApiConverter } from './PresLayerToSharedApiConverter';

export class FiltersConverter {
  public static convertFiltersPresModel(filterPms: Array<FilterInfoPresModel>): Array<Filter> {
    const filters: Array<Filter> = [];
    filterPms.forEach((filterPm) => {
      switch (filterPm.filterType) {
        case PresLayerFilterType.FLTR_Categorical: {
          filters.push(FiltersConverter.convertCategoricalFilterPresModel(filterPm));
          break;
        }

        case PresLayerFilterType.FLTR_Quantitative: {
          filters.push(FiltersConverter.convertQuantitativeFilterPresModel(filterPm));
          break;
        }

        case PresLayerFilterType.FLTR_RelativeDate: {
          filters.push(FiltersConverter.convertRelativeDateFilterPresModel(filterPm));
          break;
        }

        case PresLayerFilterType.FLTR_Hierarchical: {
          filters.push(FiltersConverter.convertHierarchicalFilterPresModel(filterPm));
          break;
        }

        default:
          return;
      }
    });
    return filters;
  }

  public static convertCategoricalFilterPresModel(filterPm: FilterInfoPresModel): CategoricalFilter {
    const categoricalFilterInfo = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(filterPm.categoricalFilterInfo);
    const selectedValues: Array<DataValue> = categoricalFilterInfo.captionedFilterValueList.map((filterValue) => {
      return FiltersConverter.convertFilterValuePresModel(filterValue);
    });
    const filter: CategoricalFilter = {
      values: selectedValues,
      isExclude: categoricalFilterInfo.exclude,
      isAllSelected: categoricalFilterInfo.allFilterOptionSelected,
      filterType: FilterType.Categorical,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
    };
    return filter;
  }

  public static convertHierarchicalFilterPresModel(filterPm: FilterInfoPresModel): HierarchicalFilter {
    const hierarchicalFilterInfo = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(filterPm.hierarchicalFilterInfo);
    const selectedValues: Array<HierarchicalDataValue> = hierarchicalFilterInfo.hierarchicalFilterValueList.map((filterValue) => {
      return FiltersConverter.convertHierarchicalFilterValuePresModel(filterValue);
    });
    const levelInfo: Array<HierarchicalLevelInfo> = hierarchicalFilterInfo.levelInfoList.map((levelInfo) => {
      return FiltersConverter.convertHierarchicalLevelInfoPresModel(levelInfo);
    });
    const filter: HierarchicalFilter = {
      dimensionName: hierarchicalFilterInfo.hiearchicalDimensionName,
      hierarchyCaption: hierarchicalFilterInfo.hierarchyCaption,
      values: selectedValues,
      levels: hierarchicalFilterInfo.levelCount,
      levelInfo: levelInfo,
      isAllSelected: hierarchicalFilterInfo.allFilterOptionSelected,
      filterType: FilterType.Hierarchical,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
    };
    return filter;
  }

  public static convertQuantitativeFilterPresModel(filterPm: FilterInfoPresModel): RangeFilter {
    const quantitativeFilterInfo = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(filterPm.quantitativeFilterInfo);
    const minValue: DataValue = FiltersConverter.convertFilterValuePresModel(quantitativeFilterInfo.captionedRangeMin);
    const maxValue: DataValue = FiltersConverter.convertFilterValuePresModel(quantitativeFilterInfo.captionedRangeMax);
    const filter: RangeFilter = {
      filterType: FilterType.Range,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
      min: minValue,
      max: maxValue,
      includeNullValues: FiltersConverter.convertIncludedNullValues(quantitativeFilterInfo.included),
    };
    return filter;
  }

  public static convertRelativeDateFilterPresModel(filterPm: FilterInfoPresModel): RelativeDateFilter {
    const relativeDateFilterInfo = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(filterPm.relativeDateFilterInfo);
    const anchorDateValue: DataValue = FiltersConverter.convertFilterValuePresModel(relativeDateFilterInfo.captionedAnchorDate);
    const filter: RelativeDateFilter = {
      filterType: FilterType.RelativeDate,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
      anchorDate: anchorDateValue,
      periodType: EnumMappings.dateStepPeriod.convert(relativeDateFilterInfo.datePeriodType),
      rangeType: EnumMappings.dateRangeType.convert(relativeDateFilterInfo.dateRangeType),
      rangeN: relativeDateFilterInfo.rangeN,
    };
    return filter;
  }

  public static convertCategoricalDomain(domainPm: CategoricalDomainPresModel): CategoricalDomain {
    const domainValues: Array<DataValue> = domainPm.captionedFilterValueList.map((filterValue) => {
      return FiltersConverter.convertFilterValuePresModel(filterValue);
    });
    return { values: domainValues };
  }

  public static convertQuantitativeDomain(domainPm: QuantitativeDomainPresModel): RangeDomain {
    const minValue: DataValue = FiltersConverter.convertFilterValuePresModel(domainPm.captionedRangeMin);
    const maxValue: DataValue = FiltersConverter.convertFilterValuePresModel(domainPm.captionedRangeMax);
    return {
      max: maxValue,
      min: minValue,
    };
  }

  // It would be nice to merge this and the method DataTypeConverter::convertValueAsStringToValue in api-internal-contract-js
  public static convertFilterValuePresModel(dvPM: FilterValuePresModel): DataValue {
    /*tslint:disable-next-line:no-any */
    let value: any;
    switch (dvPM.dataType) {
      case DataType.Boolean: {
        value = Boolean(dvPM.dataValueStr);
        break;
      }
      case DataType.Date: {
        value = new Date(dvPM.dataValueStr);
        value = Number.isNaN(value.getTime()) ? undefined : value;
        break;
      }
      case DataType.DateTime: {
        value = new Date(dvPM.dataValueStr);
        value = Number.isNaN(value.getTime()) ? undefined : value;
        break;
      }
      case DataType.Integer: {
        value = Number.parseInt(dvPM.dataValueStr, 10);
        break;
      }
      case DataType.Real: {
        value = Number.parseFloat(dvPM.dataValueStr);
        break;
      }

      case DataType.Spatial: // treat as string
      case DataType.String:
      default: {
        value = dvPM.dataValueStr;
        break;
      }
    }
    return {
      value: value,
      // TODO: no easy way of getting back aliased value from platform, will need to investigate further
      aliasedValue: '',
      formattedValue: dvPM.dataValueDisplayString,
    };
  }

  public static convertHierarchicalFilterValuePresModel(filterValue: HierarchicalFilterValuePresModel): HierarchicalDataValue {
    return {
      value: FiltersConverter.convertFilterValuePresModel(filterValue.captionedFilterValue),
      hierarchicalPath: filterValue.hierarchicalPath,
      level: filterValue.levelNumber,
    };
  }

  public static convertHierarchicalLevelInfoPresModel(aLevel: HierarchicalLevelInfoPresModel): HierarchicalLevelInfo {
    return {
      name: aLevel.levelName,
      levelSelectionState: EnumMappings.hierarchicalLevelSelectionStateType.convert(aLevel.levelSelectionState),
    };
  }

  public static convertIncludedNullValues(includeNull: IncludedValues): boolean {
    return includeNull === IncludedValues.InRangeOrNull || includeNull === IncludedValues.All || includeNull === IncludedValues.Null;
  }
}
